import { Button, Modal, Upload, message } from "antd";
import React, { useState } from "react";
import { FiDownload } from "react-icons/fi";
import { IoCloseCircleOutline, IoTrashBinOutline } from "react-icons/io5";
import { LuUpload } from "react-icons/lu";
import { MdUploadFile } from "react-icons/md";
import { RiFileEditLine } from "react-icons/ri";
import * as XLSX from "xlsx";
import apiCall from "../../components/Api";
import {
  DetailRow,
  Section,
} from "../../Pages/HealthsyServices/MedicineList/utils";
import excel from "../../assets/svg/excel.svg";
import { PrimaryButton, SecondaryButton } from "./ButtonComp";

const UploadExcel = ({ apiUrl, list_api_call, medCount = 0 }) => {
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [fileModalVisible, setFileModalVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const [actionType, setactionType] = useState("");
  const [missingIds_count, setmissingIds_count] = useState("");

  const handleBeforeUpload = (file) => {
    const isExcel =
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.type === "application/vnd.ms-excel";

    if (!isExcel) {
      message.error("You can only upload Excel files (.xls, .xlsx)");
      return Upload.LIST_IGNORE;
    }

    setFileList([file]);
    setFileModalVisible(true);
    return false;
  };

  const handleRemove = () => {
    setFileList([]);
    setFileModalVisible(false);
  };

  const handleUpload = async (action = "upload") => {
    if (!fileList.length) {
      message.error("Please select a file!");
      return;
    }

    setactionType(action);
    setUploading(true);
    setProgress(10);
    const file = fileList[0];

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        setProgress(30);
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet);
        setProgress(50);

        const fieldChecks = [
          { keyword: "product-type", field: "product_type_name" },
          { keyword: "unit", field: "unit" },
          { keyword: "manufacturer", field: "manufacturer_name" },
          { keyword: "salt-molecule", field: "salt_molecule" },
          { keyword: "medicine", fields: ["product_id", "medicine_name"] },
          { keyword: "pincode", fields: ["pincode", "delivarable"] },
        ];

        for (const check of fieldChecks) {
          if (apiUrl.includes(check.keyword)) {
            const requiredFields = Array.isArray(check.fields)
              ? check.fields
              : [check.field];

            const missingFields = requiredFields.filter(
              (field) =>
                !jsonData.length ||
                !jsonData.some((row) =>
                  Object.prototype.hasOwnProperty.call(row, field),
                ),
            );

            if (missingFields.length) {
              message.error(
                `Missing required field(s): ${missingFields.join(", ")}`,
              );
              setUploading(false);
              setProgress(0);
              return;
            }
          }
        }

        if (
          jsonData.length &&
          jsonData[0].hasOwnProperty("medicine_name") &&
          action === "upload"
        ) {
          const units = [
            ...new Set(jsonData.map((r) => r.unit).filter(Boolean)),
          ];
          const molecule = [
            ...new Set(jsonData.map((r) => r.salt_molecule).filter(Boolean)),
          ];
          const manufacturers = [
            ...new Set(
              jsonData.map((r) => r.manufacturer_name).filter(Boolean),
            ),
          ];
          const productTypes = [
            ...new Set(jsonData.map((r) => r.medicine_type).filter(Boolean)),
          ];
          try {
            await apiCall("unit/create-bulk", "POST", {
              records: units.map((u) => ({ unit: u })),
            });
            await apiCall("salt-molecule/create-bulk", "POST", {
              records: molecule.map((m) => ({ salt_molecule: m })),
            });
            await apiCall("manufacturer/create-bulk", "POST", {
              records: manufacturers.map((m) => ({ manufacturer_name: m })),
            });
            await apiCall("product-type/create-bulk", "POST", {
              records: productTypes.map((p) => ({ product_type_name: p })),
            });
          } catch (depError) {
            console.error("Error in dependency upload:", depError);
            message.error("Error uploading dependent entities");
            setUploading(false);
            setProgress(0);
            return;
          }
        }

        setProgress(80);

        let finalRecords = jsonData;

        if (action === "update") {
          const checkIds = jsonData.map((r) => r.product_id).filter(Boolean);

          try {
            const res = await apiCall("medicine/validateProductIds", "POST", {
              product_ids: checkIds,
            });

            const validSet = new Set(res?.data?.valid_ids || []);
            const validRecords = [];
            const missingIds = [];

            for (let record of jsonData) {
              if (validSet.has(record.product_id)) {
                validRecords.push(record);
              } else {
                missingIds.push(record.product_id);
              }
            }

            if (missingIds.length) {
              setmissingIds_count(missingIds.length);
              console.warn("Missing product_ids:", missingIds);
            }

            if (!validRecords.length) {
              message.error(
                "None of the product_ids were found in the database.",
              );
            }

            finalRecords = validRecords;
          } catch (err) {
            console.error("Bulk product_id validation failed:", err);
            message.error("Product ID validation failed.");
            throw err;
          }
        }
        if (!Array.isArray(finalRecords) || finalRecords.length === 0) {
          console.error("Final records is empty or invalid:", finalRecords);
          message.error("Processed records array is empty.");
          setUploading(false);
          setProgress(0);
          return;
        }
        let response;

        if (action === "delete") {
          const productIds = jsonData.map((r) => r.product_id).filter(Boolean);

          if (productIds.length === 0) {
            message.error("No valid product_id found for deletion.");
            setUploading(false);
            setProgress(0);
            return;
          }

          const records = productIds.map((id) => ({ product_id: id }));

          response = await apiCall(`medicine/delete`, "DELETE", {
            data: { records },
          });
        } else {
          const finalUrl =
            action === "upload" ? apiUrl : apiUrl.replace("create", "update");

          response = await apiCall(`${finalUrl}`, "POST", {
            records: finalRecords,
          });
        }

        setProgress(100);
        setTimeout(() => setProgress(0), 1000);
        list_api_call(e);
        setResponseData(response.data);
        setModalVisible(true);
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error(error);
      message.error("Error uploading file");
      setProgress(0);
    } finally {
      setUploading(false);
      setFileList([]);
      setFileModalVisible(false);
    }
  };

  const truncateFileName = (filename) => {
    if (!filename) return "";

    const nameWithoutExt = filename.split(".").slice(0, -1).join(".");
    return nameWithoutExt.length > 20
      ? nameWithoutExt.slice(0, 20) + "..."
      : nameWithoutExt;
  };

  return (
    <div className="d-flex uploadContent gap-3">
      {progress !== 0 ? (
        <div className="mb-2" style={{ width: 150 }}>
          <div className="progress">
            <div
              className="progress-bar progress-bar-striped progress-bar-animated bg-primarycolor "
              role="progressbar"
              style={{ width: `${progress}%` }}
              aria-valuenow={progress}
              aria-valuemin="0"
              aria-valuemax="100"
            >
              {progress}%
            </div>
          </div>
        </div>
      ) : (
        <Upload
          beforeUpload={handleBeforeUpload}
          fileList={[]}
          showUploadList={false}
          maxCount={1}
        >
          <Button icon={<LuUpload size={18} />}>Choose File</Button>
        </Upload>
      )}

      <Modal
        open={fileModalVisible}
        title={
          <h6 className="text-primarycolor fs18 fw-bold">
            Select Upload Action
          </h6>
        }
        onCancel={handleRemove}
        footer={null}
        closeIcon={<IoCloseCircleOutline size={24} />}
        centered
      >
        <div className="d-flex justify-content-between align-items-center mt-4">
          <div className="d-flex align-items-center gap-2">
            <img src={excel} alt="excel" width={24} />
            <span style={{ fontWeight: 500 }}>
              {truncateFileName(fileList[0]?.name)}
            </span>
          </div>
          <div className="d-flex gap-2 align-items-center">
            {fileList[0] && (
              <a
                href={URL.createObjectURL(fileList[0])}
                download={fileList[0].name}
                style={{ color: "#1890ff" }}
                title="Download File"
              >
                <FiDownload size={18} />
              </a>
            )}
            <IoTrashBinOutline
              onClick={handleRemove}
              style={{ cursor: "pointer", color: "red" }}
              title="Remove File"
              size={18}
            />
          </div>
        </div>

        <div className="d-flex justify-content-center gap-2 my-4">
          {medCount > 0 && apiUrl.split("/")[0] === "medicine" && (
            <div className="d-flex justify-content-center gap-3 flex-wrap">
              <Button
                className="d-flex align-items-center gap-2  border border-primarycolor bg-light  rounded"
                onClick={() => handleUpload("delete")}
              >
                <IoTrashBinOutline size={18} />
                <span>Delete</span>
              </Button>
            </div>
          )}
          {medCount > 0 && apiUrl.split("/")[0] === "medicine" && (
            <SecondaryButton
              onClick={() => handleUpload("update")}
              buttonName="Update"
              icon={<RiFileEditLine size={18} />}
              loading={uploading}
            />
          )}

          <PrimaryButton
            onClick={() => handleUpload("upload")}
            buttonName="Upload"
            icon={<MdUploadFile size={18} />}
            loading={uploading}
          />
        </div>
      </Modal>

      <Modal
        title={
          <h6 className="text-primarycolor fs18 fw-bold">Upload Summary</h6>
        }
        open={modalVisible}
        centered
        onCancel={() => setModalVisible(false)}
        footer={null}
        closeIcon={<IoCloseCircleOutline size={24} />}
      >
        {responseData ? (
          <Section title="" isLast>
            <DetailRow
              data={[
                {
                  label: "Records Uploaded:",
                  value:
                    responseData?.totalCount ||
                    responseData?.total ||
                    responseData?.data?.totalCount ||
                    0,
                },
                {
                  label: `Records ${
                    actionType === "upload"
                      ? "Created"
                      : actionType === "update"
                        ? "Updated"
                        : "deleted"
                  }:`,
                  value:
                    responseData?.successCount ||
                    responseData?.data?.deletedCount ||
                    0,
                },
                {
                  label: "Skipped Duplicated:",
                  value:
                    actionType === "delete"
                      ? responseData.data?.skippedDuplicates || 0
                      : responseData.skippedDuplicates || missingIds_count || 0,
                },
                {
                  label: "Records Failed:",
                  value:
                    actionType === "delete"
                      ? responseData.data?.failedCount || 0
                      : responseData.failedCount || 0,
                },
              ]}
            />
          </Section>
        ) : (
          <p>No response received from server.</p>
        )}
      </Modal>
    </div>
  );
};

export default UploadExcel;
